import * as React from "react";
import { useHistory, useParams } from "react-router";
import { camelCase } from "lodash";

import { PageWrapper } from "@/components/PageWrapper";
import { Fans } from "@/containers/Coach/Reputation/Fans";
import { ReviewInbox } from "./components/ReviewInbox";
import { URLS, useSocialCoachSession } from "@/utils";
import { FormLinksTable } from "./components/FormLinksTable";
import { Tabs } from "@/components/ui/tabs";
import { ChevronLeft, History, Inbox, Settings, Share2, Users } from "lucide-react";
import { Button } from "@/components/ui";
import { ShareFormLinkDialog } from "@/components/ShareFormLinkDialog";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { InboxHeaderButton } from "@/containers/Players/Reputation/components/InboxHeaderButton";
import { SocialNetworkProfiles } from "@/components/SocialNetworkProfiles";

const PlayerReputationFC: React.FC = () => {
  const history = useHistory();
  const { playerId } = useSocialCoachSession();
  const [showReviewLinkDialog, setShowReviewLinkDialog] = useState(false);

  const { tab } = useParams<{ tab: string }>();
  const [selectedTabKey, setSelectedTabKey] = useState<string>(tab === "reviews" ? "inbox" : tab || "inbox");

  React.useEffect(() => {
    if (tab) {
      if (tab === "reviews") {
        setSelectedTabKey("inbox");
      } else {
        setSelectedTabKey(tab);
      }
    }
  }, [tab]);

  React.useEffect(() => {
    history.replace(URLS.player.reputation.tab.replace(":tab?", camelCase(selectedTabKey)));
  }, [selectedTabKey]);

  return (
    <PageWrapper hideSeparator fitViewport={true}>
      <div className="w-full flex flex-row flex-wrap gap-4 justify-between items-center px-4 pt-4 pb-2 shrink-0">
        <div className="flex flex-row gap-4 items-center flex-wrap">
          {selectedTabKey === "inbox" ? (
            <div className="flex flex-row items-center gap-2">
              <div className="bg-indigo-50/50 text-primary p-1.5 rounded-lg">
                <Inbox className="size-4" />
              </div>
              <div className="text-xl font-bold text-slate-800">Review Inbox</div>
            </div>
          ) : (
            <div
              className="group flex flex-row items-center gap-1.5 cursor-pointer hover:bg-slate-50 py-1.5 px-2 -ml-2 rounded-lg transition-colors select-none"
              onClick={() => setSelectedTabKey("inbox")}
            >
              <ChevronLeft className="size-4 text-slate-400 group-hover:text-primary transition-colors" />
              <div className="text-base font-bold text-slate-600 group-hover:text-primary transition-colors">
                Back to Review Inbox
              </div>
            </div>
          )}

          <div className="flex flex-row items-center gap-1.5 pl-4 ml-2 border-l-2 border-slate-100">
            <InboxHeaderButton
              label="Fans"
              icon={Users}
              selected={selectedTabKey === "fans"}
              onClick={() => setSelectedTabKey("fans")}
            />
            <InboxHeaderButton
              label="Review Sources"
              selected={selectedTabKey === "sources"}
              icon={Settings}
              onClick={() => setSelectedTabKey("sources")}
            />
            <InboxHeaderButton
              label="Sent Links"
              icon={History}
              selected={selectedTabKey === "forms"}
              onClick={() => setSelectedTabKey("forms")}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2 items-center">
          <Button
            variant="default"
            onClick={() => {
              setShowReviewLinkDialog(true);
            }}
          >
            <Share2 className="size-4 shrink-0 text-current mr-1" />
            Share Review Link
          </Button>
        </div>
      </div>

      <Tabs
        value={selectedTabKey}
        onValueChange={(val: any) => setSelectedTabKey(val)}
        className="flex flex-col flex-1 overflow-hidden min-h-0 w-full"
      >
        <Tabs.Content
          value="inbox"
          className={cn("flex flex-col flex-1 min-h-0", selectedTabKey === "inbox" ? "overflow-hidden" : "hidden")}
        >
          {selectedTabKey === "inbox" && (
            <ReviewInbox playerId={playerId} onShareReviewLink={() => setShowReviewLinkDialog(true)} />
          )}
        </Tabs.Content>

        <Tabs.Content
          value="fans"
          className={cn("flex flex-col flex-1 min-h-0", selectedTabKey === "fans" ? "p-4 overflow-y-auto" : "hidden")}
        >
          {selectedTabKey === "fans" && <Fans isPlayerContext={true} playerId={playerId} />}
        </Tabs.Content>

        <Tabs.Content
          value="sources"
          className={cn(
            "flex flex-col flex-1 min-h-0",
            selectedTabKey === "sources" ? "p-4 overflow-y-auto" : "hidden"
          )}
        >
          {selectedTabKey === "sources" && <SocialNetworkProfiles networkFilter={["FB", "GL", "ZL"]} />}
        </Tabs.Content>

        <Tabs.Content
          value="forms"
          className={cn("flex flex-col flex-1 min-h-0", selectedTabKey === "forms" ? "p-4 overflow-y-auto" : "hidden")}
        >
          {selectedTabKey === "forms" && <FormLinksTable />}
        </Tabs.Content>
      </Tabs>

      <ShareFormLinkDialog open={showReviewLinkDialog} onOpenChange={setShowReviewLinkDialog} playerId={playerId!} />
    </PageWrapper>
  );
};

export const PlayerReputation = PlayerReputationFC;
